import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Eye, Lock, Loader2, Globe, LayoutTemplate, FileText, Wrench } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GROUP_ICON = {
  Functions: Wrench,
  'Homepage blocks': LayoutTemplate,
  Pages: FileText,
};

/**
 * WebsiteVisibilityPanel — control which public-website surfaces are visible to
 * everyone (public) vs logged-in members only. OL is the SOT; the WP theme reads
 * the resolved gate map from the public feed.
 */
export default function WebsiteVisibilityPanel({ orgId }) {
  const { isDark } = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/website-visibility/${orgId}`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load visibility settings');
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  const toggle = async (item) => {
    const next = item.visibility === 'members' ? 'public' : 'members';
    setSavingKey(item.key);
    setItems((prev) => prev.map((it) => (it.key === item.key ? { ...it, visibility: next } : it)));
    try {
      await axios.put(`${API}/api/website-visibility/${orgId}`,
        { gates: { [item.key]: next } }, { withCredentials: true });
      toast.success(`${item.label} is now ${next === 'members' ? 'members only' : 'public'}`);
    } catch (e) {
      setItems((prev) => prev.map((it) => (it.key === item.key ? { ...it, visibility: item.visibility } : it)));
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingKey(null); }
  };

  const groups = items.reduce((acc, it) => {
    (acc[it.group] = acc[it.group] || []).push(it);
    return acc;
  }, {});

  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';

  return (
    <div className="space-y-4" data-testid="website-visibility-panel">
      <Card className={card}>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-teal-500/15"><Eye className="h-5 w-5 text-teal-400" /></div>
            <div>
              <CardTitle>Website Visibility</CardTitle>
              <p className={`text-sm ${muted} mt-0.5`}>
                Choose what the public sees vs. what's reserved for logged-in members —
                pages, homepage blocks, and specific functions. Changes publish to the site
                from OmniLeagues.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
            </div>
          ) : (
            Object.entries(groups).map(([group, rows]) => {
              const GIcon = GROUP_ICON[group] || Globe;
              return (
                <div key={group}>
                  <div className={`text-[10px] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5 ${muted}`}>
                    <GIcon className="h-3.5 w-3.5" /> {group}
                  </div>
                  <div className="space-y-2">
                    {rows.map((it) => {
                      const membersOnly = it.visibility === 'members';
                      return (
                        <div key={it.key}
                             className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                               isDark ? 'bg-slate-900/40 border-slate-700' : 'bg-slate-50 border-slate-200'}`}
                             data-testid={`vis-row-${it.key}`}>
                          <div className="min-w-0">
                            <div className="text-sm font-medium">{it.label}</div>
                            <div className={`text-[11px] ${muted} truncate`}>{it.desc}</div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggle(it)}
                            disabled={savingKey === it.key}
                            className={membersOnly
                              ? 'border-amber-500/50 text-amber-500 hover:bg-amber-500/10'
                              : 'border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10'}
                            data-testid={`vis-toggle-${it.key}`}
                          >
                            {savingKey === it.key ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                              : membersOnly ? <Lock className="h-3.5 w-3.5 mr-1.5" /> : <Globe className="h-3.5 w-3.5 mr-1.5" />}
                            {membersOnly ? 'Members only' : 'Public'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
          {!loading && items.every((it) => it.group !== 'Pages') && (
            <p className={`text-[11px] ${muted}`}>
              Connect a WordPress site (Site Linking) to also gate individual pages.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
